import * as React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridOverlay
} from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import {
  HTTPHeaders
} from '../../interfaces';

import {
  PriorityRequestCount
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  NULL_DATETIME_STRING
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// UTC to local date formatting
import { useDates } from '../../hooks/UseDates';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface UsageStatisticsPriorityPeriodsProps {
  fiscalYear: string;
}

const UsageStatisticsPriorityPeriods: React.FC<UsageStatisticsPriorityPeriodsProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { dateStringToLocalDateFormatted } = useDates();
  const { isMobile } = useResponsive();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [requestCounts, setRequestCounts] = React.useState<Array<PriorityRequestCount>>([]);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      margin: theme.spacing(2, 2, 2, 2),
    },
    grid: {
      '& .super-app-theme--header': {
        backgroundColor: 'rgba(255, 7, 0, 0.55)'
      }
    },
    noRows: {
      padding: theme.spacing(2, 0, 2, 0)
    },
  };

  // #endregion

  // #region Methods

  /*
* Grid column definitions
*/
  const columns = (): Array<GridColDef> => {

    if (isMobile) {
      return [
        {
          field: 'weekendholiday',
          headerName: 'Weekend/Holiday',
          width: 200,
          type: 'string',
          align: 'center',
          renderCell: (params: GridCellParams) =>
            `${params.row.arrivalDate}-${params.row.departureDate}`
        },
        {
          field: 'individualCount',
          headerName: 'Individuals',
          width: 90,
          type: 'number',
          align: 'right',
        }
      ];

    } else {
      return [
        {
          field: 'weekendholiday',
          headerName: 'Weekend/Holiday',
          headerAlign:'center',
          width: 200,
          type: 'string',
          align: 'center',
          renderCell: (params: GridCellParams) =>
            `${params.row.arrivalDate}-${params.row.departureDate}`
        },
        {
          field: 'nights',
          headerName: 'Nights',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'householdCount',
          headerName: 'Households',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'individualCount',
          headerName: 'Individuals',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'memberCount',
          headerName: 'Members',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'guestCount',
          headerName: 'Guests',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'percentCapacity',
          headerName: '% Capacity',
          headerAlign: 'center',
          width: 100,
          type: 'string',
          align: 'right',
        },
        {
          field: 'pendingCount',
          headerName: 'Pending',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'cancelledCount',
          headerName: 'Cancelled',
          headerAlign: 'center',
          width: 100,
          type: 'number',
          align: 'right',
        },
        {
          field: 'signupDeadline',
          headerName: 'Deadline',
          headerAlign: 'center',
          width: 225,
          type: 'string',
          align: 'center',
          renderCell: (params: GridCellParams) => (
            renderDeadline(params)
          )
        },
      ];
    }

  }

  /*
* Custom title for datagrid
*/
  const DataGridTitle = () => {
    return (
      <Box style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="button">Weekend/Holiday Usage Statistics</Typography>
      </Box>
    )
  }

  /*
  * Fetch priority period request counts
  */
  const fetchStatistics = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const params = new URLSearchParams();
    params.append('year', props.fiscalYear);

    const url = new URL(`${API_URL}/v1/requests/usagestatistics/priorityperiods`);
    url.search = params.toString();

    await fetch(url.toString(),
      {
        method: 'GET'
        , cache: 'no-cache'
        , headers: headers
        , mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Usage statistics for priority periods not retrieved');
        }
        return response.json()
      })
      .then((rc: Array<PriorityRequestCount>) => {
        setIsLoading(false);
        setRequestCounts(rc);
      })
      .catch(err => {
        setIsLoading(false);
        throw new Error('Usage statistics for priority periods not retrieved');
      });
  }

  /*
* Overlay displayed when no data
*/
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No weekend/holidays statistics to display</div>
      </GridOverlay>
    );
  }

  const renderDeadline = (params: GridCellParams) => {
    // 1. Check if it's a string
    // 2. Check if it's not the NULL constant
    if (typeof params.value === 'string' && params.value !== NULL_DATETIME_STRING) {
      return dateStringToLocalDateFormatted(params.value, 'MM/dd/yyyy HH:mm');
    }

    // Fallback for everything else (null, undefined, or the NULL_DATETIME_STRING)
    return 'n/a';
  }

  // #endregion

  // #region Event handlers

  React.useEffect(() => {
    if (props.fiscalYear !== EMPTY_STRING) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fiscalYear]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <DataGridTitle />
      <DataGrid
        columns={columns()}
        slots={{
          noRowsOverlay: noRowsOverlay
        }}
        density="compact"
        disableColumnSelector
        disableRowSelectionOnClick
        getRowId={(row) => row.priorityPeriodID}
        hideFooter
        loading={isLoading}
        rows={requestCounts}
        sx={{
          ...styles.grid,
          height: 'auto', // Explicitly tell the Grid its height is auto
          '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
        }}
      />
    </Box>
  );

  // #endregion

}

export default UsageStatisticsPriorityPeriods;